import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StatutPipeline } from '@cockpit/shared';
import { SourcesContact, TerritoiresContact, TypesClient } from '@cockpit/shared';
import { api } from '../lib/api';
import { KanbanColumn } from '../components/contacts/kanban/KanbanColumn';
import { LABEL_SOURCE_CONTACT, LABEL_TERRITOIRE_CONTACT, LABEL_TYPE_CLIENT } from '../lib/format';

export function ContactsPipelinePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const filters = useMemo(
    () => ({
      territoire: searchParams.get('territoire') ?? '',
      type_client: searchParams.get('type_client') ?? '',
      source: searchParams.get('source') ?? '',
    }),
    [searchParams],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const query = useQuery({
    queryKey: ['contacts', 'pipeline', filters],
    queryFn: () =>
      api.contacts.pipeline({
        territoire: filters.territoire === '' ? undefined : filters.territoire,
        type_client: filters.type_client === '' ? undefined : filters.type_client,
        source: filters.source === '' ? undefined : filters.source,
      }),
    staleTime: 30_000,
  });

  const move = useMutation({
    mutationFn: ({ id, statut }: { id: number; statut: StatutPipeline }) =>
      api.contacts.pipelineMove(id, statut),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Erreur déplacement');
    },
  });

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (over === null) return;
    const newStatut = (over.data.current?.statut ?? null) as StatutPipeline | null;
    const contactId = (active.data.current?.contactId ?? null) as number | null;
    const currentStatut = (active.data.current?.currentStatut ?? null) as StatutPipeline | null;
    if (newStatut === null || contactId === null || newStatut === currentStatut) return;
    setError(null);
    move.mutate({ id: contactId, statut: newStatut });
  };

  const setFilter = (key: 'territoire' | 'type_client' | 'source', value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Pipeline CRM</h1>
          <p className="text-xs text-slate-500 mt-1">
            Glisser-déposer une carte pour changer son statut. Une interaction "Autre" trace le
            mouvement automatiquement.
          </p>
        </div>
      </header>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <select
          value={filters.territoire}
          onChange={(e): void => setFilter('territoire', e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-100"
        >
          <option value="">Tous territoires</option>
          {TerritoiresContact.map((t) => (
            <option key={t} value={t}>
              {LABEL_TERRITOIRE_CONTACT[t] ?? t}
            </option>
          ))}
        </select>
        <select
          value={filters.type_client}
          onChange={(e): void => setFilter('type_client', e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-100"
        >
          <option value="">Tous types</option>
          {TypesClient.map((t) => (
            <option key={t} value={t}>
              {LABEL_TYPE_CLIENT[t] ?? t}
            </option>
          ))}
        </select>
        <select
          value={filters.source}
          onChange={(e): void => setFilter('source', e.target.value)}
          className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-100"
        >
          <option value="">Toutes sources</option>
          {SourcesContact.map((s) => (
            <option key={s} value={s}>
              {LABEL_SOURCE_CONTACT[s] ?? s}
            </option>
          ))}
        </select>
      </div>

      {error !== null && (
        <div className="mb-4 text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isError && <div className="text-sm text-red-400">Erreur de chargement.</div>}

      {query.isSuccess && (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {query.data.columns.map((col) => (
              <KanbanColumn
                key={col.statut}
                statut={col.statut}
                items={col.items}
                totalValeurEstimeeCentimes={col.total_valeur_estimee_centimes}
              />
            ))}
          </div>
        </DndContext>
      )}
    </div>
  );
}
